/**
 * SpineOffscreenRenderer
 *
 * Singleton service that manages a hidden PixiJS Application for rendering
 * Spine skeleton snapshots and animation previews as blob URLs.
 *
 * The scene view is HTML/CSS based, so we render Spine display objects
 * offscreen and extract them as images.
 */

import { Application, ImageSource } from 'pixi.js';
import {
  Spine,
  SpineTexture,
  TextureAtlas,
  AtlasAttachmentLoader,
  SkeletonBinary,
  SkeletonJson,
  SkeletonData,
  Physics,
} from '@esotericsoftware/spine-pixi-v8';
import { readFile, readFileAsArrayBuffer, getProjectRoot } from './FileSystemService';

// ============================================================================
// Types
// ============================================================================

interface SnapshotResult {
  url: string;
  width: number;
  height: number;
}

type FrameListener = (url: string, width: number, height: number) => void;

interface AnimationSession {
  spine: Spine;
  skeletonPath: string;
  animationName: string;
  loop: boolean;
  timeScale: number;
  mixDuration: number;
  listeners: Map<string, FrameListener>; // subscriberId → callback
  lastFrameUrl: string | null;
  // Fixed canvas dimensions calculated from max bounds across animation
  fixedW: number;
  fixedH: number;
  fixedOffsetX: number;
  fixedOffsetY: number;
}

interface CachedSkeletonData {
  skeletonData: SkeletonData;
  refCount: number;
}

// ============================================================================
// Helpers
// ============================================================================

function getParentFolder(filePath: string): string {
  const normalized = filePath.replace(/\\/g, '/');
  const lastSlash = normalized.lastIndexOf('/');
  return lastSlash >= 0 ? normalized.substring(0, lastSlash + 1) : '';
}

function parsePathSegments(path: string): string[] {
  return path
    .replace(/\\/g, '/')
    .split('/')
    .filter((segment) => segment.length > 0 && segment !== '.');
}

async function getFileHandle(relativePath: string): Promise<FileSystemFileHandle | null> {
  const projectRoot = getProjectRoot();
  if (!projectRoot) return null;

  const segments = parsePathSegments(relativePath);
  if (segments.length === 0) return null;

  const fileName = segments.pop()!;
  let currentHandle: FileSystemDirectoryHandle = projectRoot;

  for (const segment of segments) {
    try {
      currentHandle = await currentHandle.getDirectoryHandle(segment);
    } catch {
      return null;
    }
  }

  try {
    return await currentHandle.getFileHandle(fileName);
  } catch {
    return null;
  }
}

/**
 * Load an image from the project file system and return an HTMLImageElement.
 * We need an actual loaded image to create SpineTexture from its TextureSource.
 */
async function loadImageFromPath(relativePath: string): Promise<HTMLImageElement> {
  const fileHandle = await getFileHandle(relativePath);
  if (!fileHandle) {
    throw new Error(`Image file not found: ${relativePath}`);
  }
  const file = await fileHandle.getFile();
  const blobUrl = URL.createObjectURL(file);

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error(`Failed to load image: ${relativePath}`));
    };
    img.src = blobUrl;
    // Keep blobUrl alive — it will be revoked when the renderer disposes
  });
}

// ============================================================================
// SpineOffscreenRenderer
// ============================================================================

class SpineOffscreenRenderer {
  private app: Application | null = null;
  private initPromise: Promise<void> | null = null;
  private skeletonCache = new Map<string, CachedSkeletonData>();
  private blobUrlsToRevoke = new Set<string>();
  // Single active animation session — multiple consumers share frames
  private activeSession: AnimationSession | null = null;
  // Pending session info — tracks what's being set up async so new callers
  // can join before setup completes (React Strict Mode double-invoke fix)
  private pendingKey: string | null = null;
  private pendingListeners = new Map<string, FrameListener>();
  private subscriberCounter = 0;
  // Generation counter: async setups from prior calls see a stale generation
  // and abort, preventing zombie sessions.
  private currentGeneration = 0;
  private loopRafId: number = 0;

  // ---- Lazy initialization ----

  private async ensureApp(): Promise<Application> {
    if (this.app) return this.app;

    if (!this.initPromise) {
      this.initPromise = (async () => {
        const app = new Application();
        await app.init({
          width: 256,
          height: 256,
          backgroundAlpha: 0,
          antialias: true,
          // Don't add canvas to DOM — offscreen only
        });
        this.app = app;
      })();
    }

    await this.initPromise;
    return this.app!;
  }

  // ---- Atlas & Skeleton loading ----

  /**
   * Load a TextureAtlas from project files.
   *
   * spine-core's TextureAtlas constructor only takes atlas text.
   * After creation, we need to manually set textures on each page
   * by loading the referenced PNG files.
   */
  private async loadAtlas(
    atlasPath: string,
    texturePaths: string[]
  ): Promise<TextureAtlas> {
    const atlasText = await readFile(atlasPath);
    const atlasFolder = getParentFolder(atlasPath);

    // Create the TextureAtlas (parses page/region data)
    const atlas = new TextureAtlas(atlasText);

    // Build a filename -> path map from provided texturePaths
    const pathByName = new Map<string, string>();
    for (const tp of texturePaths) {
      const fileName = tp.replace(/\\/g, '/').split('/').pop()!;
      pathByName.set(fileName, tp);
    }

    // Set textures on each atlas page
    for (const page of atlas.pages) {
      const texPath = pathByName.get(page.name) || (atlasFolder + page.name);
      try {
        const img = await loadImageFromPath(texPath);
        // Create a PixiJS ImageSource from the HTMLImageElement, then wrap as SpineTexture
        const imageSource = new ImageSource({ resource: img });
        const spineTexture = SpineTexture.from(imageSource);
        page.setTexture(spineTexture);
      } catch (err) {
        console.warn(`[SpineOffscreenRenderer] Failed to load texture page "${page.name}":`, err);
      }
    }

    return atlas;
  }

  /** Public access to cached skeleton data — used by useSpineCanvas */
  async getSkeletonData(
    skeletonPath: string,
    atlasPath: string,
    texturePaths: string[]
  ): Promise<SkeletonData> {
    return this.loadSkeletonData(skeletonPath, atlasPath, texturePaths);
  }

  private async loadSkeletonData(
    skeletonPath: string,
    atlasPath: string,
    texturePaths: string[]
  ): Promise<SkeletonData> {
    // Check cache
    const cacheKey = skeletonPath;
    const cached = this.skeletonCache.get(cacheKey);
    if (cached) {
      cached.refCount++;
      return cached.skeletonData;
    }

    // Load atlas
    const atlas = await this.loadAtlas(atlasPath, texturePaths);
    const atlasLoader = new AtlasAttachmentLoader(atlas);

    // Load skeleton
    const isBinary = skeletonPath.endsWith('.skel');
    let skeletonData: SkeletonData;

    if (isBinary) {
      const buffer = await readFileAsArrayBuffer(skeletonPath);
      const skeletonBinary = new SkeletonBinary(atlasLoader);
      skeletonData = skeletonBinary.readSkeletonData(new Uint8Array(buffer));
    } else {
      const jsonText = await readFile(skeletonPath);
      const skeletonJson = new SkeletonJson(atlasLoader);
      skeletonData = skeletonJson.readSkeletonData(jsonText);
    }

    // Cache it
    this.skeletonCache.set(cacheKey, { skeletonData, refCount: 1 });

    return skeletonData;
  }

  // ---- Snapshot rendering ----

  async renderSnapshot(
    skeletonPath: string,
    atlasPath: string,
    texturePaths: string[],
    options?: {
      skinName?: string;
      animationName?: string;
      time?: number;
    }
  ): Promise<SnapshotResult> {
    const app = await this.ensureApp();
    const skeletonData = await this.loadSkeletonData(skeletonPath, atlasPath, texturePaths);

    // Create Spine display object
    const spine = new Spine(skeletonData);
    spine.autoUpdate = false;

    // Apply skin
    if (options?.skinName) {
      try {
        spine.skeleton.setSkinByName(options.skinName);
        spine.skeleton.setSlotsToSetupPose();
      } catch {
        // Skin not found — use default
      }
    }

    // Apply animation at specific time if requested
    if (options?.animationName) {
      try {
        spine.state.setAnimation(0, options.animationName, false);
        spine.update(options.time ?? 0);
      } catch {
        // Animation not found
      }
    } else {
      spine.update(0);
    }

    // Two-pass render: first render to measure actual bounds, then reposition
    app.stage.removeChildren();
    app.stage.addChild(spine);

    // Pass 1: Render at large canvas with spine at origin to measure bounds
    const maxDim = Math.max(skeletonData.width || 1024, skeletonData.height || 1024) * 2;
    app.renderer.resize(maxDim, maxDim);
    spine.x = maxDim / 2;
    spine.y = maxDim / 2;
    app.renderer.render(app.stage);

    // Get actual rendered bounds
    const bounds = spine.getBounds();
    const bw = bounds.maxX - bounds.minX;
    const bh = bounds.maxY - bounds.minY;

    // If bounds are valid, use them for the final render
    const pad = 4;
    const canvasW = Math.max(1, Math.ceil(bw + pad * 2));
    const canvasH = Math.max(1, Math.ceil(bh + pad * 2));

    // Pass 2: Render at exact size with proper positioning
    app.renderer.resize(canvasW, canvasH);
    spine.x = pad - bounds.minX;
    spine.y = pad - bounds.minY;
    app.renderer.render(app.stage);

    // Extract to blob URL
    const url = await this.extractToBlobUrl(app);

    // Clean up
    app.stage.removeChildren();
    spine.destroy();

    return { url, width: canvasW, height: canvasH };
  }

  // ---- Animation loop ----

  startAnimation(
    skeletonPath: string,
    atlasPath: string,
    texturePaths: string[],
    animationName: string,
    loop: boolean,
    onFrame: (url: string, width: number, height: number) => void,
    options?: {
      skinName?: string;
      timeScale?: number;
      mixDuration?: number;
    }
  ): string {
    const subscriberId = `spine-sub-${++this.subscriberCounter}`;
    const sessionKey = `${skeletonPath}:${animationName}`;

    // Join existing active session for the same skeleton + animation
    const s = this.activeSession;
    if (s && s.skeletonPath === skeletonPath && s.animationName === animationName) {
      s.listeners.set(subscriberId, onFrame);
      if (s.lastFrameUrl) {
        onFrame(s.lastFrameUrl, s.fixedW, s.fixedH);
      }
      console.log(`[SpineAnim] ${subscriberId} joined active session (${s.listeners.size} listeners)`);
      return subscriberId;
    }

    // Join pending session that's still setting up (same skeleton + animation)
    if (this.pendingKey === sessionKey) {
      this.pendingListeners.set(subscriberId, onFrame);
      console.log(`[SpineAnim] ${subscriberId} joined pending session (${this.pendingListeners.size} listeners)`);
      return subscriberId;
    }

    // Different animation or nothing exists — create new session
    this.destroyActiveSession();
    this.pendingKey = sessionKey;
    this.pendingListeners.clear();
    this.pendingListeners.set(subscriberId, onFrame);
    const generation = ++this.currentGeneration;

    console.log(`[SpineAnim] ${subscriberId} creating new session for "${animationName}"`);

    this.setupAnimationSession(
      generation,
      skeletonPath,
      atlasPath,
      texturePaths,
      animationName,
      loop,
      options
    );

    return subscriberId;
  }

  private destroyActiveSession(): void {
    this.pendingKey = null;
    this.pendingListeners.clear();
    if (!this.activeSession) return;
    cancelAnimationFrame(this.loopRafId);
    this.activeSession.spine.destroy();
    if (this.app) this.app.stage.removeChildren();
    this.activeSession = null;
  }

  private async setupAnimationSession(
    generation: number,
    skeletonPath: string,
    atlasPath: string,
    texturePaths: string[],
    animationName: string,
    loop: boolean,
    options?: {
      skinName?: string;
      timeScale?: number;
      mixDuration?: number;
    }
  ): Promise<void> {
    try {
      const app = await this.ensureApp();
      if (this.currentGeneration !== generation) return;

      const skeletonData = await this.loadSkeletonData(skeletonPath, atlasPath, texturePaths);
      if (this.currentGeneration !== generation) return;

      const spine = new Spine(skeletonData);
      spine.autoUpdate = false;

      // Apply skin
      if (options?.skinName) {
        try {
          spine.skeleton.setSkinByName(options.skinName);
          spine.skeleton.setSlotsToSetupPose();
        } catch {
          // Use default skin
        }
      }

      // Set default mix
      if (options?.mixDuration !== undefined) {
        spine.state.data.defaultMix = options.mixDuration;
      }

      // Start animation
      let animDuration = 1.33;
      try {
        const entry = spine.state.setAnimation(0, animationName, loop);
        if (entry?.animation) {
          animDuration = entry.animation.duration || 1.33;
        }
      } catch {
        console.warn(`[SpineOffscreenRenderer] Animation not found: ${animationName}`);
        return;
      }

      // Pre-calculate maximum bounds across the entire animation
      // Sample several time points to find the largest bounding box
      app.stage.removeChildren();
      app.stage.addChild(spine);
      const maxDim = Math.max(skeletonData.width || 1024, skeletonData.height || 1024) * 2;
      app.renderer.resize(maxDim, maxDim);
      spine.x = maxDim / 2;
      spine.y = maxDim / 2;

      let globalMinX = Infinity, globalMinY = Infinity;
      let globalMaxX = -Infinity, globalMaxY = -Infinity;
      const sampleCount = 10;
      for (let i = 0; i <= sampleCount; i++) {
        const t = (animDuration * i) / sampleCount;
        // Reset and advance to sample time
        spine.state.setAnimation(0, animationName, loop);
        spine.update(t);
        app.renderer.render(app.stage);
        const b = spine.getBounds();
        if (b.maxX > b.minX && b.maxY > b.minY) {
          globalMinX = Math.min(globalMinX, b.minX);
          globalMinY = Math.min(globalMinY, b.minY);
          globalMaxX = Math.max(globalMaxX, b.maxX);
          globalMaxY = Math.max(globalMaxY, b.maxY);
        }
      }

      // Fallback if bounds were never valid
      if (!isFinite(globalMinX)) {
        const dw = skeletonData.width || 256;
        const dh = skeletonData.height || 256;
        globalMinX = (maxDim / 2) - dw / 2;
        globalMinY = (maxDim / 2) - dh / 2;
        globalMaxX = (maxDim / 2) + dw / 2;
        globalMaxY = (maxDim / 2) + dh / 2;
      }

      const pad = 8;
      let fixedW = Math.max(1, Math.ceil(globalMaxX - globalMinX + pad * 2));
      let fixedH = Math.max(1, Math.ceil(globalMaxY - globalMinY + pad * 2));
      let fixedOffsetX = pad - globalMinX;
      let fixedOffsetY = pad - globalMinY;

      // Cap canvas size to prevent huge toDataURL calls that tank FPS.
      // Scale down if needed — the <img> will stretch back to display size.
      const MAX_DIM = 512;
      if (fixedW > MAX_DIM || fixedH > MAX_DIM) {
        const scale = MAX_DIM / Math.max(fixedW, fixedH);
        fixedW = Math.ceil(fixedW * scale);
        fixedH = Math.ceil(fixedH * scale);
        fixedOffsetX *= scale;
        fixedOffsetY *= scale;
        spine.scale.set(scale);
      }

      // Abort if superseded during bounds calculation
      if (this.currentGeneration !== generation) {
        spine.destroy();
        return;
      }

      // Reset animation to start for playback
      spine.state.setAnimation(0, animationName, loop);
      spine.update(0);

      // Collect all pending listeners that accumulated during async setup
      const listeners = new Map(this.pendingListeners);
      this.pendingKey = null;
      this.pendingListeners.clear();

      const session: AnimationSession = {
        spine,
        skeletonPath,
        animationName,
        loop,
        timeScale: options?.timeScale ?? 1,
        mixDuration: options?.mixDuration ?? 0.2,
        listeners,
        lastFrameUrl: null,
        fixedW,
        fixedH,
        fixedOffsetX,
        fixedOffsetY,
      };

      this.activeSession = session;

      // Set up stage once
      app.renderer.resize(fixedW, fixedH);
      app.stage.removeChildren();
      app.stage.addChild(spine);
      spine.x = fixedOffsetX;
      spine.y = fixedOffsetY;

      console.log(`[SpineAnim] session ready: ${fixedW}x${fixedH} listeners=${session.listeners.size}`);

      // Start render loop — single session, one render per frame
      const FPS_INTERVAL = 1000 / 30;
      let lastTimestamp = performance.now();
      let frameCount = 0;

      const renderLoop = (timestamp: number) => {
        // Session was replaced or destroyed
        if (this.activeSession !== session) return;

        const elapsed = timestamp - lastTimestamp;
        if (elapsed >= FPS_INTERVAL) {
          const delta = (elapsed / 1000) * session.timeScale;
          lastTimestamp = timestamp;
          frameCount++;

          session.spine.update(delta);
          app.renderer.render(app.stage);
          const url = this.extractFrameUrl(app);

          session.lastFrameUrl = url;
          // Broadcast to all listeners
          for (const listener of session.listeners.values()) {
            listener(url, session.fixedW, session.fixedH);
          }

          if (frameCount <= 3 || frameCount % 60 === 0) {
            console.log(`[SpineAnim] frame #${frameCount} listeners=${session.listeners.size} delta=${delta.toFixed(3)}`);
          }
        }

        this.loopRafId = requestAnimationFrame(renderLoop);
      };

      this.loopRafId = requestAnimationFrame(renderLoop);
    } catch (error) {
      console.error(`[SpineOffscreenRenderer] Failed to setup animation session:`, error);
    }
  }

  stopAnimation(subscriberId: string): void {
    // Remove from pending if still setting up
    this.pendingListeners.delete(subscriberId);

    const session = this.activeSession;
    if (!session) return;

    // Remove this subscriber
    session.listeners.delete(subscriberId);

    // If no listeners remain, tear down the session
    if (session.listeners.size === 0) {
      this.destroyActiveSession();
    }
  }

  setTimeScale(_subscriberId: string, timeScale: number): void {
    if (this.activeSession) {
      this.activeSession.timeScale = timeScale;
    }
  }

  setMixDuration(_subscriberId: string, mixDuration: number): void {
    if (this.activeSession) {
      this.activeSession.mixDuration = mixDuration;
      this.activeSession.spine.state.data.defaultMix = mixDuration;
    }
  }

  // ---- Extraction helper ----

  /**
   * Synchronous frame extraction for the animation loop.
   * Uses extract.canvas() for reliable GPU readback, then WebP for fast encoding.
   */
  private extractFrameUrl(app: Application): string {
    const canvas = app.renderer.extract.canvas(app.stage) as HTMLCanvasElement;
    return canvas.toDataURL('image/webp', 0.8);
  }

  /** Async extraction for one-off snapshots (uses toBlob for smaller memory). */
  private async extractToBlobUrl(app: Application): Promise<string> {
    const canvas = app.renderer.extract.canvas(app.stage) as HTMLCanvasElement;
    return new Promise<string>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          resolve(canvas.toDataURL('image/png'));
        }
      }, 'image/png');
    });
  }

  // ---- Cache management ----

  evictSkeleton(skeletonPath: string): void {
    const cached = this.skeletonCache.get(skeletonPath);
    if (cached) {
      cached.refCount--;
      if (cached.refCount <= 0) {
        this.skeletonCache.delete(skeletonPath);
      }
    }
  }

  // ---- Metadata extraction (for binary .skel files) ----

  async extractMetadata(
    skeletonPath: string,
    atlasPath: string,
    texturePaths: string[]
  ): Promise<{
    width: number;
    height: number;
    skins: string[];
    animations: Array<{ name: string; duration: number }>;
    bones: Array<{ name: string; parent: string | null }>;
    slots: Array<{ name: string; bone: string }>;
  }> {
    const skeletonData = await this.loadSkeletonData(skeletonPath, atlasPath, texturePaths);

    return {
      width: skeletonData.width,
      height: skeletonData.height,
      skins: skeletonData.skins.map((s) => s.name),
      animations: skeletonData.animations.map((a) => ({
        name: a.name,
        duration: a.duration,
      })),
      bones: skeletonData.bones.map((b) => ({
        name: b.name,
        parent: b.parent?.name ?? null,
      })),
      slots: skeletonData.slots.map((s) => ({
        name: s.name,
        bone: s.boneData.name,
      })),
    };
  }

  // ---- Cleanup ----

  dispose(): void {
    // Stop active animation
    this.destroyActiveSession();

    // Revoke all blob URLs
    for (const url of this.blobUrlsToRevoke) {
      URL.revokeObjectURL(url);
    }
    this.blobUrlsToRevoke.clear();

    // Clear caches
    this.skeletonCache.clear();

    // Destroy app
    if (this.app) {
      this.app.destroy(true);
      this.app = null;
      this.initPromise = null;
    }
  }
}

// Singleton export
export const spineOffscreenRenderer = new SpineOffscreenRenderer();
